/* everaw-tap.js -- acknowledge the tap before the next document paints.
   v21, 2026-08-26. REWRITTEN after adversarial review found three ways to leave
   visible damage on screen; the notes below are why each rule is shaped as it is.

   WHY THIS EXISTS
   We are a multi-page Liquid theme, so every navigation is a document teardown
   and cold start: ~63 requests, ~436 KB, stylesheets re-parsed and scripts
   re-executed, on a mid-range Android inside the Instagram webview (78% of our
   mobile sessions). We cannot delete that -- an SPA router would, but Judge.me,
   Chatwoot, GTM, the Meta pixel and FlexyPe all assume a fresh document, and
   FlexyPe sits on the checkout path. What we CAN delete is the silence in front
   of it.

   THE BUG THAT SHAPED THIS FILE
   v1 listened for click and submit in the CAPTURE phase. `evt.defaultPrevented`
   is by definition false in capture -- nothing downstream has run yet -- so
   every AJAX interaction looked like a navigation:
     - product-form.js preventDefaults submit and POSTs to /cart/add.js, so
       EVERY Add to Cart started a bar that nothing ever finished. It sat at 80%
       across the viewport for the rest of the session.
     - cart-drawer.js preventDefaults the header cart anchor to open the drawer.
       Same stuck bar.
   Both listeners are now BUBBLE phase, which is the only phase where
   defaultPrevented carries information. A watchdog backs that up, because a
   handler could navigate via location.assign without preventing anything.

   v1 also had no touchend handler, so the pressed state survived any tap that
   did not navigate -- which is most taps. Every non-navigating control stayed
   visibly dimmed until the next tap somewhere else. */

(function () {
  'use strict';

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  function reduced() { return !!(mq && mq.matches); }   // read live, not at load

  /* Feedback uses brightness, NOT opacity. GSAP's scroll reveal writes an inline
     `opacity` onto elements, and an inline style beats any class selector, so an
     opacity-based press state silently did nothing on every animated element.
     The doubled class raises specificity without !important. */
  var CSS =
    '.ev-tapped.ev-tapped{filter:brightness(.82);transition:filter 60ms linear}' +
    '.ev-progress{position:fixed;top:0;left:0;right:0;height:2.5px;z-index:2147483000;' +
    'background:var(--ev-progress-color,#036A38);transform:scaleX(0);transform-origin:0 50%;' +
    'pointer-events:none;opacity:0}' +
    '.ev-progress.is-running{opacity:1;transform:scaleX(.8);will-change:transform;' +
    'transition:transform 2.4s cubic-bezier(.15,.85,.3,1),opacity 120ms linear}' +
    '.ev-progress.is-done{transform:scaleX(1);opacity:0;' +
    'transition:transform 140ms ease-out,opacity 220ms ease-out 120ms}';

  var injected = false;
  function injectCSS() {
    if (injected) return;
    injected = true;
    var s = document.createElement('style');
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  /* ---- progress bar ------------------------------------------------------ */

  var bar = null;
  var watchdog = null;

  function getBar() {
    if (bar && bar.isConnected) return bar;
    injectCSS();
    bar = document.createElement('div');
    bar.className = 'ev-progress';
    bar.setAttribute('aria-hidden', 'true');   // decorative, keep it out of a11y tree
    document.body.appendChild(bar);
    return bar;
  }

  function startBar() {
    if (reduced()) return;
    var b = getBar();
    b.classList.remove('is-done');
    /* rAF rather than a forced reflow: v1 read offsetWidth inside the click
       handler, which synchronously lays out the page on the exact critical path
       this file exists to shorten. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { b.classList.add('is-running'); });
    });

    /* Belt and braces for anything that cancels navigation AFTER bubbling, or
       navigates by script and never unloads. Without this a single missed case
       leaves the bar on screen for the session -- which is exactly what v1 did. */
    clearTimeout(watchdog);
    watchdog = setTimeout(resetBar, 8000);
  }

  function finishBar() {
    clearTimeout(watchdog);
    if (!bar) return;
    bar.classList.remove('is-running');
    bar.classList.add('is-done');
  }

  function resetBar() {
    clearTimeout(watchdog);
    if (!bar) return;
    bar.classList.remove('is-running', 'is-done');
  }

  /* ---- pressed state ----------------------------------------------------- */

  var pressed = null;

  function clearPressed() {
    if (!pressed) return;
    pressed.classList.remove('ev-tapped');
    pressed = null;
  }

  function pressTarget(el) {
    if (!el) return;
    injectCSS();
    clearPressed();
    pressed = el;
    el.classList.add('ev-tapped');
  }

  /* ---- which interactions are real navigations --------------------------- */

  function navigatingAnchor(evt) {
    /* Bubble phase, so this is now meaningful: cart-drawer.js, product-form.js
       and everaw-chat have all had their say by the time we run. */
    if (evt.defaultPrevented) return null;
    if (evt.button && evt.button !== 0) return null;
    if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return null;

    var a = evt.target && evt.target.closest && evt.target.closest('a[href]');
    if (!a) return null;
    if (a.hasAttribute('download')) return null;
    if (a.target && a.target !== '_self') return null;

    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') return null;
    if (/^(javascript|mailto|tel|sms|whatsapp):/i.test(href)) return null;

    var url;
    try { url = new URL(a.href, window.location.href); } catch (e) { return null; }
    if (url.origin !== window.location.origin) return null;
    if (url.pathname === window.location.pathname && url.search === window.location.search) return null;

    return a;
  }

  /* ---- wiring ------------------------------------------------------------ */

  /* FlexyPe renders .flexy-btn as a <button>, not an anchor, and owns its click
     via stopImmediatePropagation plus its own skeleton state. Excluded here so
     we never double up on the checkout affordance. (v1 put this check only in
     the anchor path, where it could never match -- a no-op.) */
  var PRESSABLE = 'a[href], button, summary, [role="button"], .flexy-btn';

  document.addEventListener('touchstart', function (evt) {
    var t = evt.target;
    var el = t && t.closest && t.closest(PRESSABLE);
    if (el && !el.classList.contains('flexy-btn') && !el.closest('.flexy-btn')) pressTarget(el);
  }, { passive: true });

  document.addEventListener('touchend', clearPressed, { passive: true });
  document.addEventListener('touchmove', clearPressed, { passive: true });
  document.addEventListener('touchcancel', clearPressed, { passive: true });

  document.addEventListener('click', function (evt) {
    if (navigatingAnchor(evt)) startBar();
  }, false);   // bubble

  document.addEventListener('submit', function (evt) {
    if (evt.defaultPrevented) return;          // AJAX carts and forms stop here
    var f = evt.target;
    if (!f || f.tagName !== 'FORM') return;
    /* getAttribute, not f.method: a form containing <input name="method">
       clobbers the property with an element, and .toLowerCase() would throw. */
    var m = (f.getAttribute('method') || 'get').toLowerCase();
    /* Both verbs navigate. v1 only handled POST, which meant predictive search
       (method="get") -- a real navigation -- got nothing, while the AJAX POSTs
       it did catch were not navigations at all. */
    if (m === 'get' || m === 'post') startBar();
  }, false);   // bubble

  /* pagehide covers unload and bfcache entry. beforeunload is deliberately NOT
     used: it is redundant here and disqualifies bfcache in some engines. */
  window.addEventListener('pagehide', finishBar);

  window.addEventListener('pageshow', function (evt) {
    if (evt.persisted) { resetBar(); clearPressed(); }
  });

  window.EverawTap = { start: startBar, finish: finishBar, reset: resetBar };
}());
